// Profile page (/profile)
const profilePage = {
  loadError: 'Your profile could not be loaded. Try refreshing the page.',
  common: {
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving...',
    previous: 'Previous',
    next: 'Next',
    pages: 'Pages',
  },
  nav: {
    label: 'Profile sections',
    groups: {
      'takvim-ajanda': 'Calendar',
      dosyalar: 'Files',
      projelerim: 'My Projects',
      'izin-yonetimi': 'Leave',
      'masraf-yonetimi': 'Expenses',
      'iletisim-bildirim': 'Messages',
      'dilekce-yonetimi': 'Petitions',
      'cv-yonetimi': 'CV / GCP / IATA',
      kargolarim: 'My Shipments',
    },
    tabs: {
      takvim: 'My Calendar',
      ajandam: 'Agenda',
      'izin-talep': 'Request Leave',
      'izin-taleplerim': 'My Leave Requests',
      'masraf-talep': 'Claim Expense',
      'masraf-takip': 'My Expenses',
      'masraf-talepleri': 'Expense Approvals',
      mesajlar: 'Messages',
      bildirimler: 'Announcements',
      'sistem-hata-bildirimleri': 'Report a Bug',
      sikayetlerim: 'My Petitions',
      'gelen-sikayetler': 'Incoming Petitions',
      'cv-tr': 'Turkish CV',
      'cv-en': 'English CV',
      gcp: 'GCP',
      iata: 'IATA',
    },
  },
  sections: {
    expenseApprovals: {
      title: 'Expense Approvals',
      description: 'Review employees’ expense claims and approve or reject them.',
    },
    'cv-tr': {
      title: 'Turkish CV',
      description: 'Upload your current Turkish CV. It is used for project applications and audits.',
    },
    'cv-en': {
      title: 'English CV',
      description: 'Upload your English CV for international sponsors.',
    },
    gcp: {
      title: 'GCP Certificate',
      description: 'Upload your Good Clinical Practice (GCP) certificate and renew it before it expires.',
    },
    iata: {
      title: 'IATA Certificates',
      description: 'Upload the IATA certificates needed to ship samples. You can add more than one file.',
    },
    cargo: {
      title: 'My Shipments',
      description: 'Track the shipments sent to you and by you.',
    },
  },
  hero: {
    label: 'Profile card',
    noName: 'My Profile',
    photoAlt: 'Profile photo',
    changePhoto: 'Change photo',
    photoSaved: 'Your profile photo was updated.',
    photoError: 'The profile photo could not be uploaded.',
    changePassword: 'Change Password',
    stats: {
      projects: 'Active projects',
      sops: 'SOPs read',
      messages: 'Unread messages',
    },
    contacts: {
      title: 'My contact details',
      phone: 'Phone',
      emergency: 'Emergency contact',
      email: 'E-mail',
      home: 'Home address',
      work: 'Work address',
      primary: 'Main address',
      none: 'Not on record',
      notAdded: 'Not added yet',
      edit: 'Edit',
      add: 'Add',
    },
    completion: {
      title: 'Profile completion',
      complete: 'Your profile is complete',
      seeMissing: 'See what’s missing',
      explain: 'Based on your personal details, photo and the training assigned to you.',
      rows: {
        personal: 'Personal details',
        photo: 'Profile photo',
        education: 'SOPs and training',
      },
      personalDetail: '{{filled}}/{{total}} fields filled',
      educationDetail: '{{completed}}/{{total}} completed',
      noAssignments: 'No SOPs or training assigned to you',
      done: 'Done',
      missing: 'Missing',
      todo: 'To complete it',
      suggestions: {
        photo: 'Upload a profile photo',
        emergencyPhone: 'Add an emergency contact',
        address: 'Add your home address',
        education: 'Go to pending SOPs and training',
      },
      othersMissing: 'For other missing details such as your name, role, e-mail or phone, contact your manager.',
    },
  },
  photo: {
    title: 'Profile Photo',
    subtitle: 'See how it will look before saving',
    onProfile: 'On your profile',
    onCard: 'On cards',
    inMenu: 'In the menu',
    another: 'Choose another photo',
    save: 'Save Photo',
    saving: 'Saving...',
    notImage: 'The file you chose isn’t a photo. Please choose a JPG or PNG image.',
    tooLarge: 'The photo is too large (15 MB at most). Please choose a smaller file.',
    noPreview: 'Your browser can’t preview this photo; you can still save it.',
    tips: 'Choose a well-lit photo where your face is clearly visible. It is resized to a suitable size when saved.',
  },
  dialogs: {
    userNotFound: 'Your user record was not found.',
    address: {
      title: 'My Addresses',
      subtitle: 'Home and work address',
      home: 'Home address',
      homePlaceholder: 'Street, number, district / city',
      homeRequired: 'The home address can’t be empty',
      work: 'Work address',
      workPlaceholder: 'Address of the site you work at (if any)',
      workHint: 'Optional',
      primary: 'My main address',
      primaryHint: 'The address you choose shows as the "Main address" in Employee Management and on your employee card.',
      primaryHomeOnly: 'With no work address, your main address is your home address.',
      saved: 'Your addresses were updated.',
      saveError: 'The address could not be updated.',
    },
    phone: {
      title: 'Emergency Contact',
      subtitle: 'The number to call when you can’t be reached',
      number: 'Phone number',
      placeholder: '+90 5xx xxx xx xx',
      hint: 'It can be a relative’s number. Used only in emergencies.',
      saved: 'Your emergency contact was updated.',
      saveError: 'The emergency contact could not be updated.',
    },
    password: {
      title: 'Change Password',
      subtitle: 'Enter your current password too, for your security',
      current: 'Current password',
      new: 'New password',
      confirm: 'New password (again)',
      show: 'Show passwords',
      hide: 'Hide passwords',
      ruleLength: 'At least 6 characters',
      ruleMatch: 'Both new passwords match',
      submit: 'Change Password',
      saved: 'Your password was changed.',
      errors: {
        required: 'Please fill in all fields.',
        mismatch: 'The new passwords don’t match.',
        length: 'The password must be at least 6 characters.',
        wrong: 'Your current password is wrong.',
        failed: 'The password could not be changed. Please try again.',
      },
    },
  },
  calendar: {
    title: 'My Calendar',
    description: 'Your visits, jobs and approved leave by month. Tap a day to list everything on it; tap an entry to open it.',
    loading: 'Loading calendar...',
    gridLabel: 'Monthly calendar',
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    today: 'Today',
    viewLabel: 'View',
    views: {
      month: 'Month',
      list: 'List',
    },
    actions: {
      addVisit: 'Add Visit',
      addJob: 'Add Job',
      excel: 'Excel',
      excelHint: 'Download this month’s visits as Excel',
    },
    excelEmpty: 'There are no visits to download for this month.',
    summary: {
      label: 'This month',
      total: 'visits',
      planned: 'planned',
      inProgress: 'in progress',
      overdue: 'not completed',
      completed: 'completed',
      screenFail: 'screen fail',
      jobs: 'jobs',
    },
    entryCount_one: '{{count}} entry',
    entryCount_other: '{{count}} entries',
    noEntriesShort: 'no entries',
    openWindows_one: '{{count}} open window',
    openWindows_other: '{{count}} open windows',
    agenda: {
      planned: 'Planned for this day',
      nothingPlanned: 'No visits or jobs are planned for this day.',
      openWindows_one: 'Visit with an open window ({{count}})',
      openWindows_other: 'Visits with an open window ({{count}})',
      openWindowsHint: 'Their visit day is another day, but they can be done on this day too.',
      showAll: 'Show all ({{count}})',
      showLess: 'Show less',
    },
    options: {
      windows: 'Windows',
      windowsHint: 'Show visit windows as bars. When off, each visit shows only on its visit day.',
      hideCompleted: 'Hide completed',
      hideCompletedHint: 'Hides completed visits and jobs from the calendar.',
    },
    projectFilter: {
      button: 'Projects',
      someHidden: 'Projects ({{shown}}/{{total}})',
      title: 'Projects shown',
      showAll: 'Show all',
    },
    help: {
      button: 'How to read it?',
      title: 'How to read the calendar',
      bar: 'Each visit is one bar across its window. The light part is the window in which it can be done; the dark part is the planned visit day.',
      done: 'Completed visits are green; the green tick marks the day the visit was done.',
      stripe: 'The coloured stripe on the left shows the project. Dashed bars are your own jobs. Tap a bar to open it.',
      tips: 'Tip: tapping a day lists what is planned on it and, separately, the visits whose window is open. If the calendar is crowded, turn "Windows" off or "Hide completed" on.',
    },
    more_one: '+{{count}} more',
    more_other: '+{{count}} more',
    emptyDay: 'Nothing on this day.',
    emptyMonth: 'Your calendar is empty this month.',
    emptyMonthHint: 'New visits and jobs are listed here day by day.',
    projectFallback: 'Project',
    noPatient: 'No patient ID',
    screenFail: 'Screen fail',
    visitOrdinal: 'Visit {{n}}',
    dayLabel: {
      first: 'Visit day',
      after: 'Day +{{n}}',
      before: 'Day {{n}}',
    },
    jobLabel: 'Job',
    jobDay: 'Day {{n}}',
    jobStatus: {
      done: 'Done',
      ongoing: 'In progress',
    },
    pendingDates: 'Missing date: {{items}}',
    pending: {
      ecrf: 'e-CRF',
      sourceDoc: 'Source document',
      signature: 'e-CRF signature',
    },
    status: {
      screenFail: 'Screen fail',
      notCompleted: 'Not completed',
      ongoing: 'In progress',
      completedNoDate: 'Completed · completion date not recorded',
      completedThisDay: 'Completed on this day: {{date}}',
      completedWindowClosed: 'Completed: {{date}} · window closed',
      missingSuffix: ' · Missing: {{items}}',
      completedOn: 'Completed: {{date}}',
    },
    tooltip: {
      visitDay: 'Visit day',
      window: 'Window',
      project: 'Project',
      patient: 'Patient ID',
      visit: 'Visit',
      day: 'Day',
      status: 'Status',
      time: 'Time',
    },
    visitTypes: {
      hastane: 'Site Visit',
      home: 'Home Visit',
      telefon: 'Phone Visit',
      unplanning: 'Unplanned Visit',
      handover: 'Handover Visit',
    },
    onLeave: 'On leave',
    leaveBanner: 'You are on leave on this day',
    leaveDays_one: '{{count}} day',
    leaveDays_other: '{{count}} days',
    legend: {
      title: 'Colours and projects',
      planned: 'Planned',
      inProgress: 'In progress',
      overdue: 'Not completed',
      completedDay: 'Day it was done',
      completed: 'Completed',
      windowClosed: 'Window closed',
      screenFail: 'Screen fail',
      job: 'Job',
      leave: 'Day on leave',
      note: 'The bold, outlined chip is the visit’s own day; the lighter ones are the other days of its window. The coloured stripe on the left shows the project.',
      projects: 'This month’s projects (tap to hide or show)',
      hideProject: 'Hide this project’s visits',
      showProject: 'Show this project’s visits',
      hiddenCount_one: '{{count}} project hidden',
      hiddenCount_other: '{{count}} projects hidden',
    },
    visitForm: {
      title: 'New Visit',
      subtitle: 'Plan a visit for a patient in your project',
      saving: 'Saving...',
      saved_one: 'Visit added.',
      saved_other: '{{count}} visits added.',
      sections: {
        who: 'Project and patient',
        when: 'Date and visit window',
        details: 'Visit details',
        options: 'Options',
      },
      project: 'Project',
      chooseProject: 'Choose a project',
      patient: 'Patient ID',
      patientOptional: 'Choose a patient (optional)',
      patientHint: 'Only the patients you have visits with or added yourself are listed.',
      chooseProjectFirst: 'Choose a project first',
      patientsLoading: 'Loading patients...',
      noPatients: 'You have no patients in this project',
      noPatientsHint: 'Use + next to it to add a patient.',
      addPatient: 'Add a patient',
      visitDate: 'Visit date',
      visitDateHint: 'The day the visit is planned for',
      windowStart: 'Window start',
      windowEnd: 'Window end',
      quickWindow: 'Quick window:',
      sameDay: 'Same day',
      plusMinus: '±{{n}} days',
      windowSummary: 'The window opens {{before}} days before and closes {{after}} days after ({{total}} days in total).',
      visitType: 'Visit type',
      choose: 'Choose',
      visitNumber: 'Visit number',
      visitNumberPlaceholder: 'e.g. 1, 2, 3',
      visitNumberHint: 'The next number is suggested once a patient is chosen.',
      suggestedNext: 'This patient’s next visit in this project: {{n}}. You can change it.',
      suggested: 'Suggested: {{n}}',
      screenFail: 'Screen fail?',
      screenFailNote: 'Entries marked as screen fail don’t count as visits; they are recorded as work done.',
      info: 'Note',
      infoPlaceholder: 'A short note about the visit',
      repeat: 'Repeats',
      repeatEnd: 'Closing visit date',
      repeatEndHint: 'Repeats are created up to this date.',
      repeatEvery: 'Repeat every',
      repeatUnit: 'Unit',
      repeatDayHint: 'With days, the next visit starts the window length plus this many days later.',
      units: {
        day: 'Days',
        week: 'Weeks',
        month: 'Months',
      },
      googleCalendar: 'Also add to my Google Calendar',
      googleHint: 'After saving, Google Calendar opens in a new tab with the details filled in; just press "Save" there.',
      errors: {
        dates: 'Please fill in all the dates.',
        window: 'The visit date must be between the window start and end.',
        repeatPeriod: 'The repeat interval must be greater than 0.',
        save: 'The visit could not be added.',
      },
      mirror: {
        created: 'Hybrid project: the visit was also added to the unblinded colleague’s calendar.',
        createdNamed: 'Hybrid project: the visit was also added to the unblinded colleague’s calendar ({{names}}).',
        skipped: 'Hybrid project: the unblinded colleague already has a visit for this patient and date.',
        failed: 'Your visit was added, but it could not be copied to the unblinded colleague’s calendar. Please let them know.',
      },
    },
    jobForm: {
      title: 'New Job',
      subtitle: 'Add a personal job to your calendar',
      jobTitle: 'Job title',
      jobTitlePlaceholder: 'e.g. Deliver documents to the site',
      startDate: 'Start date',
      endDate: 'End date',
      endDateHint: 'Same as the start for a one-day job',
      time: 'Time',
      timeHint: 'Optional; used to order the day',
      help: 'Jobs show only on your own calendar. When you finish one, tap it and mark it as done.',
      added: 'The job was added to your calendar.',
      addError: 'The job could not be added.',
    },
    jobDetail: {
      title: 'Job details',
      status: 'Status',
      time: 'Time',
      startDate: 'Start',
      endDate: 'End',
      markDone: 'Mark as done',
      markOpen: 'Mark as not done',
      markedDone: 'The job was marked as done.',
      markedOpen: 'The job was marked as in progress.',
      updateError: 'The job status could not be updated.',
      delete: 'Delete Job',
      deleteTitle: 'Delete this job?',
      deleteMessage: '"{{title}}" will be removed from your calendar. This cannot be undone.',
      deleted: 'The job was deleted.',
      deleteError: 'The job could not be deleted.',
    },
  },
  tutorial: {
    cta: {
      eyebrow: 'Video guide · {{minutes}} minutes',
      title: 'Learn the calendar and the visit form step by step',
      text: 'A short video shows, screen by screen, how to read the calendar, open a new visit, and fill in and complete the visit form.',
      watch: 'Watch the video',
      later: 'Later',
      button: 'Video guide',
      buttonHint: 'How to use the calendar and the visit form? Watch the video.',
      watched: 'Watched',
    },
    player: {
      title: 'Calendar and Visit Form Guide',
      subtitle: 'Step-by-step video walkthrough',
      play: 'Play',
      pause: 'Pause',
      replay: 'Watch from the start',
      prevChapter: 'Previous chapter',
      nextChapter: 'Next chapter',
      seek: 'Video position',
      speed: 'Playback speed',
      captionsOn: 'Hide captions',
      captionsOff: 'Show captions',
      mute: 'Mute',
      unmute: 'Unmute',
      volume: 'Volume',
      voiceUnavailable: 'No English narration voice in this browser; captions are on.',
      fullscreen: 'Full screen',
      exitFullscreen: 'Exit full screen',
      chapters: 'Chapters',
      chapterOf: 'Chapter {{current}} of {{total}}',
      ended: 'That’s the end',
      endedText: 'You are ready to use your calendar. Pick a chapter from the list to watch it again.',
      backToCalendar: 'Back to the calendar',
      keyboard: 'Shortcuts: Space play / pause · ← → 5 seconds · M mute / unmute',
      demoNote: 'Projects, patients and physicians in the video are examples.',
    },
    chapters: {
      intro: 'Introduction',
      overview: 'Calendar overview',
      reading: 'Reading the calendar',
      day: 'A day’s plan',
      addVisit: 'Opening a new visit',
      visitForm: 'Filling in the visit form',
      tips: 'Remember',
    },
    beats: {
      intro: 'In this short video you will see, step by step, how to use the calendar, open a new visit, and fill in and complete the visit form.',
      overview: 'The Calendar tab on your profile page shows your visits, jobs and approved leave for the month on a single screen.',
      months: 'Use the arrows at the top to move between months. When you are on another month, the Today button takes you straight back.',
      summary: 'The summary just below shows how many visits you have this month, and how many are planned, in progress or completed.',
      bars: 'Each visit is a single bar across the window in which it can be done. The light part is the window, the dark part is the planned visit day. Hover over a bar to see its details.',
      colors: 'The colour tells the status: blue is planned, yellow is in progress, red is past its window but not completed, and green is completed. The green tick marks the day the visit was done.',
      stripes: 'The coloured stripe on the left of a bar shows which project the visit belongs to. Dashed bars are jobs you added yourself.',
      declutter: 'If the calendar gets crowded, turn Windows off and each visit shows only on its own day. Hide completed and the Projects filter tidy the screen too.',
      day: 'Tap a day and its details open in the panel on the right.',
      dayLists: 'At the top are the visits and jobs planned for that day; below are the visits with an open window. Their visit day is another day, but they can be done on this day too.',
      addOpen: 'To open a new visit, press Add Visit at the top right. If you select a day first, the visit date is filled in with that day.',
      addWho: 'Choose the project first, then the patient. Only your own patients are listed; use the plus button next to it for a new patient.',
      addWhen: 'The visit date comes filled in with the day you selected; change it if needed. Then set the visit window: the quick options set one, such as plus or minus three days, with a single tap.',
      addDetails: 'Choose the visit type. The visit number is suggested for you from the patient’s previous visits.',
      addSave: 'For visits that repeat regularly, you can turn on Repeats. Finally press Save, and the visit appears on your calendar right away.',
      formOpen: 'When you go to the visit, tap its bar on the calendar; the Visit Details window opens.',
      formRail: 'The form goes step by step. In the list on the left, orange steps have missing fields and a green tick means the step is complete. The progress percentage is at the top.',
      formTodo: 'On the To-do step, tick the checks you have done. Ticks are saved instantly; you don’t need to press Save for them.',
      formMissing: 'On the Visit Form step, required fields that are still empty are listed at the top as orange labels. Tap a label and it takes you straight to that field.',
      formFields: 'Enter the visit type, source document, travel type, physician and hospital. When you choose the hospital, the city is filled in for you.',
      formExtras: 'If you used materials, add them on Stock; if you shipped something, on Shipments; if you had expenses, on Expenses. These steps are optional.',
      formEval: 'Finally, answer the questions marked with a star on the Visit Evaluation step. Each answer raises the progress.',
      formComplete: 'You can save your work at any time with Save. When the progress reaches one hundred percent, press Complete Visit.',
      formDone: 'A completed visit turns green on the calendar, and the green tick shows the day it was done.',
      tipsDeadline: 'Remember: after the visit window ends you have seventy-two hours to complete the visit. After that, the visit becomes read-only.',
      tipsEnd: 'To remember the colours, use the How to read it button, and watch this video again whenever you like. Good luck!',
    },
    stage: {
      datePlaceholder: 'dd/mm/yyyy',
      profile: 'Profile',
      jobDocs: 'Site document drop-off',
      jobTraining: 'Training',
      doctor: 'Dr. Ayşe Kaya',
      hospital: 'Ankara City Hospital',
      city: 'Ankara',
      toastCompleted: 'Visit completed!',
      introText: 'Reading the calendar · Opening a visit · Filling in and completing the visit form',
      deadlineTitle: 'Completion period',
      window: 'Visit window',
      lastDay: 'Last day of the window',
      grace: '+72 hours',
      canComplete: 'You can complete it',
      locked: 'Read-only',
      endTitle: 'You’re ready!',
      endText: 'This video is always available on the Calendar tab.',
    },
  },
  cv: {
    types: {
      'CV-TR': {
        name: 'Turkish CV',
        upload: 'Upload Turkish CV',
        list: 'My Turkish CV',
        description: 'Upload your Turkish CV as PDF, DOC or DOCX. Ministry applications use the Ministry CV form.',
        empty: 'No Turkish CV uploaded yet',
      },
      'CV-EN': {
        name: 'English CV',
        upload: 'Upload English CV',
        list: 'My English CV',
        description: 'Upload your English CV as PDF, DOC or DOCX. Sponsors usually ask for the TransCelerate format.',
        empty: 'No English CV uploaded yet',
      },
      GCP: {
        name: 'GCP',
        upload: 'Upload GCP',
        list: 'My GCP certificate',
        description: 'Upload your GCP certificate as PDF, DOC or DOCX. When you renew it, delete the old one and upload the new one.',
        empty: 'No GCP certificate uploaded yet',
      },
      IATA: {
        name: 'IATA',
        upload: 'Upload IATA',
        list: 'My IATA certificates',
        description: 'Upload your IATA certificates as PDF, DOC or DOCX. You can pick several files at once.',
        empty: 'No IATA certificate uploaded yet',
      },
    },
    count_one: '{{count}} document',
    count_other: '{{count}} documents',
    uploaded: 'Uploaded',
    loading: 'Loading documents...',
    emptyText: 'Pick your file in the upload area and upload it; it is listed here.',
    actions: {
      view: 'View',
      download: 'Download',
      delete: 'Delete',
    },
    rules: {
      maxSize: '10 MB at most',
      formats: 'PDF, DOC or DOCX',
      multiple: 'Several files allowed',
      single: 'One file',
    },
    templateTr: 'Download the Ministry CV form template',
    templateEn: 'Download the TransCelerate CV template',
    templateHint: 'Fill in the template and upload it below.',
    locked: 'Your {{type}} is already uploaded. Delete the current file to upload a new one.',
    drop: 'Choose a file or drag it here',
    dropNow: 'Drop the file',
    dropHint: 'PDF, DOC or DOCX · 10 MB at most',
    selectedCount_one: '{{count}} file selected',
    selectedCount_other: '{{count}} files selected',
    remove: 'Remove',
    removeAll: 'Remove all',
    uploading: 'Uploading...',
    confirmDelete: {
      title: 'Delete this document?',
      message: '“{{name}}” will be deleted. This can’t be undone.',
      confirm: 'Yes, delete',
    },
    toast: {
      loadError: 'The documents could not be loaded.',
      badType: '{{name}}: only PDF, DOC or DOCX files can be uploaded.',
      tooLarge: '{{name}}: the file must be smaller than 10 MB.',
      onlyOne: 'You can choose only one file; the first one was selected.',
      chooseFile: 'Please choose a file.',
      exists: 'There is already a {{type}} file. Delete it first to upload a new one.',
      uploaded_one: '{{type}} document uploaded.',
      uploaded_other: '{{count}} {{type}} documents uploaded.',
      uploadError: 'The {{type}} could not be uploaded: {{message}}',
      deleted: '{{type}} document deleted.',
      deleteError: 'The {{type}} could not be deleted.',
    },
  },
  quiz: {
    title: 'End-of-Reading Assessment',
    loading: 'Preparing the questions...',
    loadError: 'The questions could not be loaded. Please try again.',
    questionOf: 'Question {{current}} of {{total}}',
    passScore: 'Pass mark {{score}}%',
    choices: 'Answers',
    previous: 'Previous',
    next: 'Next',
    finish: 'Finish',
    percent: '{{value}}%',
    correctOf: '{{correct}} of {{total}} correct',
    passed: 'Passed',
    failHint: 'The correct answers aren’t shown because the same question may come up again. Review the document and try again with new questions.',
    correctAnswer: 'Correct answer: {{answer}}',
    complete: 'Complete',
    backToDocument: 'Back to the Document',
    retry: 'Try Again with New Questions',
  },
  education: {
    title: 'Training',
    duration: '{{minutes}} min {{seconds}} s',
    percent: '{{value}}%',
    play: 'Play',
    pause: 'Pause',
    back10: '10 seconds back',
    forward10: '10 seconds forward',
    mute: 'Mute',
    unmute: 'Unmute',
    volume: 'Volume',
    fullscreen: 'Full screen',
    exitFullscreen: 'Exit full screen',
    videoError: 'The video could not be loaded. Please try again later.',
    notVideo: 'This SOP isn’t a video. Use the button below to open the document.',
    viewDocument: 'View Document',
    currentSection: 'Current section',
    learningProgress: 'Learning progress',
    tabsLabel: 'Training panel',
    tabs: {
      content: 'Contents',
      notes: 'Notes',
      bookmarks: 'Bookmarks',
    },
    sections: {
      introduction: 'Introduction',
      coreConcepts: 'Core concepts',
      practice: 'Practical application',
      summary: 'Summary',
    },
    quizPromptTitle: 'Knowledge test',
    quizPromptText: 'You have watched more than 80% of this training. Take a short quiz to complete it.',
    startQuiz: 'Start Quiz',
    takeQuizToComplete: 'Take the Quiz to Complete',
    markCompleted: 'Mark as Completed',
    certificateTitle: 'Certificate earned!',
    certificateText: 'You have completed this training. You can now press “Mark as completed” below.',
    notesTitle: 'My notes',
    notesHint: 'Saved when you leave the field',
    notesPlaceholder: 'Take notes while watching...',
    saveNotes: 'Save Notes',
    bookmarksTitle: 'My bookmarks',
    addBookmark: 'Add bookmark',
    addBookmarkShort: 'Add',
    goTo: 'Go',
    noBookmarks: 'No bookmarks yet. Add bookmarks in the video to jump back to important moments.',
    watchedBadge: '{{value}}% done',
    expiredTitle: 'Deadline passed',
    expiredText: 'The deadline for this training has passed.',
    expiredDeadline: 'Deadline: {{date}}',
    showChapters: 'Show chapters',
    hideChapters: 'Hide chapters',
    contents: 'Contents',
    noChapters: 'No chapters are defined for this training.',
    watched: 'Watched',
    loadingProgress: 'Loading your progress...',
    popQuizTitle: 'Pop Quiz',
    popQuizSubtitle: 'Answer this question correctly to continue',
    choices: 'Answers',
    submitAnswer: 'Submit Answer',
    continue: 'Continue',
    rewatch: 'Watch This Part Again',
    finalQuiz: 'Final exam',
    finalDone: 'Completed · {{score}}%',
    finalLocked: 'The exam opens once 95% of the video has been watched.',
    startExam: 'Start the Exam',
    passScore: 'Pass mark {{score}}%',
    questionOf: 'Question {{current}} of {{total}}',
    previous: 'Previous',
    next: 'Next',
    finishExam: 'Finish the Exam',
    congrats: 'Congratulations!',
    sorry: 'Not this time',
    scoreLabel: 'Score',
    passedText: 'You passed the exam and earned your certificate!',
    failedText: 'You need at least {{score}}% to pass. Watch the video again and retake the exam.',
    viewCertificate: 'View Certificate',
    completeEducation: 'Complete the Training',
    rewatchVideo: 'Watch the Video Again',
    retake: 'Try Again',
    certificateModalTitle: 'Certificate',
    certificateHeading: 'Certificate of Achievement',
    certificateIntro: 'This certificate confirms that',
    participant: 'Participant',
    certificateOf: 'has completed the training',
    certificateScore: 'with a score of {{score}}%.',
    certificateDate: 'Date: {{date}}',
    qualityManagement: 'Quality Management',
    toast: {
      bookmarkAdded: 'Bookmark added.',
      notesSaved: 'Notes saved.',
      resumed: 'Resuming where you left off: {{time}}',
      noSkip: 'You can’t skip to parts you haven’t watched yet.',
      chapterLocked: 'Complete the previous chapters to watch this one.',
      wrongAnswer: 'Wrong answer! Please watch this part again.',
      certificate: 'Congratulations! You earned your certificate!',
    },
  },
  messenger: {
    search: 'Search people...',
    today: 'Today',
    yesterday: 'Yesterday',
    historyNotLoaded: 'Your chat history hasn’t been loaded yet.',
    loadHistory: 'Load Chat History',
    conversations: 'Chats',
    allUsers: 'Everyone',
    noUsers: 'No one found.',
    pickTitle: 'Pick a chat',
    pickText: 'Tap someone in the list on the left to start writing; you can send files and make voice or video calls.',
    typingShort: 'typing...',
    typing: '{{name}} is typing',
    someone: 'Someone',
    showPhoto: 'Show profile photo',
    voiceCall: 'Voice call',
    videoCall: 'Video call',
    emptyChat: 'No messages with {{name}} yet. Write the first one.',
    callEnded: 'Call with {{name}} ended',
    voiceInvite: '{{name}} is inviting you to a voice call',
    videoInvite: '{{name}} is inviting you to a video call',
    joinCall: 'Join',
    removeFile: 'Remove file',
    attach: 'Attach a file',
    placeholder: 'Type a message...',
    send: 'Send',
    conference: {
      active: 'Meeting in progress',
      host: 'Started by {{name}}',
      join: 'Join',
      end: 'End',
      start: 'Start a Meeting',
      starting: 'Starting...',
      pickSomeone: 'Please choose at least one participant.',
      title: 'Meeting Participants',
      subtitle: 'Only the people you choose can see the meeting',
      search: 'Search employees...',
      selectAll: 'Select all',
      deselectAll: 'Clear all',
      selected_one: '{{count}} person selected',
      selected_other: '{{count}} people selected',
      startWith: 'Start the Meeting ({{count}})',
    },
    broadcast: {
      open: 'Send a Broadcast',
      title: 'Send a Broadcast',
      recipients_one: 'Goes to {{count}} person',
      recipients_other: 'Goes to {{count}} people',
      info: 'The message is sent to everyone separately; each person sees it in their own chat.',
      placeholder: 'Write your message...',
      removeFile: 'Remove file',
      attach: 'Attach File',
      sendAll: 'Send to Everyone',
      sending: 'Sending...',
      sentTitle: 'Message sent!',
      sentText_one: 'Your broadcast reached {{count}} person.',
      sentText_other: 'Your broadcast reached {{count}} people.',
      history: 'Sent broadcasts ({{count}})',
      people_one: '{{count}} person',
      people_other: '{{count}} people',
      confirmDelete: 'This message will be deleted from {{count}} chats. Are you sure?',
      delete: 'Yes, delete',
      deleting: 'Deleting...',
      deleteHint: 'Delete this broadcast',
    },
  },
  projectVisits: {
    title: 'Project Visits',
    help: 'Open every detail of a completed visit with "Details". Pick a date range to narrow the list; the Excel file contains the visits shown.',
    total: 'Total Visits: {{count}}',
    completed_one: '{{count}} completed',
    completed_other: '{{count}} completed',
    ongoing_one: '{{count}} in progress',
    ongoing_other: '{{count}} in progress',
    excel: 'Download Excel',
    close: 'Close',
    loading: 'Loading visits',
    startDate: 'Start Date',
    endDate: 'End Date',
    clearDates: 'Clear dates',
    columns: {
      date: 'Date',
      type: 'Visit Type',
      patient: 'Patient ID',
      hospital: 'Hospital',
      city: 'City',
      status: 'Status',
      action: 'Action',
    },
    status: {
      done: 'Completed',
      ongoing: 'In Progress',
    },
    detail: 'Details',
    detailDisabled: 'Details are available once the visit is completed',
    empty: 'No visits found for this project',
    emptyHint: 'Visits opened in this project will be listed here.',
    noMatch: 'No visits in this date range',
    noMatchHint: 'Change or clear the dates.',
    details: {
      title: 'Visit Details',
      excel: 'Download Table',
      sections: {
        hospital: 'Hospital Visit Details',
        home: 'Home Visit Details',
        issues: 'Issue & Correction Tracking',
        basic: 'Basic Information',
        todo: 'To-do List',
        other: 'Other Information',
      },
      preVisit: 'Before the visit',
      duringVisit: 'During and after the visit',
      done: 'Done',
      notDone: 'Not done',
      todoCount: '{{done}} / {{total}} completed',
      fields: {
        projectName: 'Project Name',
        centerId: 'Site ID',
        visitId: 'Visit ID',
        visitType: 'Visit Type',
        siteCoordinator: 'Site Coordinator',
        patientId: 'Patient ID',
        screenFail: 'Screen Fail?',
        dropPatient: 'Drop Patient?',
        visitDate: 'Visit Date',
        visitStartTime: 'Visit Start Time',
        protocolDeviation: 'Protocol Deviation',
        capaOpened: 'CAPA Opened?',
        comments: 'Comments',
        ecrfDate: 'eCRF Completion Date',
        ecrfTime: 'eCRF Completion Time',
        sourceDocDate: 'Source Doc. Completion Date',
        sourceDocTime: 'Source Doc. Completion Time',
        signatureDate: 'Signature Completion Date',
        signatureTime: 'Signature Completion Time',
        processEndDate: 'Process End Date',
        cycleTime: 'Cycle Time (days)',
        issueOccurred: 'Issue Occurred?',
        slaTarget: 'SLA Target (days)',
        slaMet: 'SLA Met?',
        slaOverdue: 'SLA Overdue?',
        protocolNo: 'Protocol No.',
        siteId: 'Site ID',
        windowOpen: 'Window Open Date',
        targetDate: 'Target Date',
        windowClose: 'Window Close Date',
        subjectStatus: 'Subject Status',
        visitStartDate: 'Visit Start Date',
        visitEndDate: 'Visit End Date',
        visitEndTime: 'Visit End Time',
        durationHours: 'Visit Duration - Hours',
        durationMinutes: 'Visit Duration - Minutes',
        issueDuringVisit: 'Issue During the Visit?',
        issueDeviation: 'Did the Issue Cause a Protocol Deviation?',
        issueSource: 'Source of the Issue',
        smoCorrections: 'SMO Correction Requests',
        qualityCheck: 'Quality Manager Check',
        craRequest: 'CRA Request',
        correctionReason: 'Correction Reason / Comment',
        initialSource: 'Initial Source Received',
        latestRevision: 'Latest Revision Request from CPM',
        sourceFinalized: 'Source Finalised',
        capaTaken: 'Action / CAPA Taken?',
        dcfCreated: 'DCF Created?',
        isClosed: 'Closed?',
        capaStart: 'Start Date',
        capaClose: 'Close Date',
        targetAchieved: 'Target Achieved?',
        severity: 'Severity',
        date: 'Date',
        startTime: 'Start Time',
        endTime: 'End Time',
        doctor: 'Doctor',
        hospital: 'Hospital',
        city: 'City',
        travelType: 'Travel Type',
        status: 'Status',
        sourceDocument: 'Source Document',
        sourceDocumentTime: 'Source Document Completion Time',
        information: 'Notes',
        createdAt: 'Created',
        lastUpdated: 'Last Updated',
        completedAt: 'Completed',
        firstVisitDate: 'First Visit Date',
        employeeName: 'Employee',
        selectedCra: 'Selected CRA',
        period: 'Period',
        option: 'Option',
      },
    },
  },
  agenda: {
    title: 'Agenda',
    description: 'Add an event to your Google Calendar quickly. No sign-in needed: Google Calendar opens with the details filled in.',
    openCalendar: 'Open My Google Calendar',
    eventTitle: 'Event title',
    eventTitlePlaceholder: 'e.g. Project meeting',
    eventDescription: 'Description',
    eventDescriptionPlaceholder: 'Notes about the event',
    start: 'Start date and time',
    end: 'End date and time',
    add: 'Add to Google Calendar',
    opened: 'Google Calendar opened in a new tab. Press its "Save" button to keep the event.',
    howTo: 'How does it work?',
    steps: {
      fill: 'Fill in the event details.',
      add: 'Press "Add to Google Calendar".',
      save: 'Press "Save" on the Google Calendar page that opens.',
      done: 'The event is added to your calendar.',
    },
    note: 'This section keeps no records; the event goes straight to the calendar of your Google account.',
    errors: {
      title: 'An event title is required.',
      start: 'A start date and time are required.',
      end: 'An end date and time are required.',
      order: 'The end must be after the start.',
      past: 'The start can’t be in the past.',
      url: 'The calendar link could not be created.',
    },
  },
  files: {
    title: 'My Files',
    description: 'SOPs, training, forms, policies and job descriptions assigned to your role. Tap a category to open its list below.',
    categories: {
      sop: {
        title: 'SOP Files',
        description: 'Standard operating procedures',
      },
      education: {
        title: 'Training',
        description: 'Video training and development content',
      },
      forms: {
        title: 'Forms',
        description: 'Standard forms and documents',
      },
      policies: {
        title: 'Policies',
        description: 'Company policies',
      },
      jobDescriptions: {
        title: 'Job Descriptions',
        description: 'The job description of your position',
      },
    },
    cards: {
      sopStat: '{{read}}/{{total}} read',
      educationStat: '{{done}}/{{total}} completed',
      count_one: '{{count}} document',
      count_other: '{{count}} documents',
      new_one: '{{count}} new',
      new_other: '{{count}} new',
    },
    help: {
      sop: 'Open an SOP, read it to the end, then tick "I have read and understood". A short quiz opens; once you pass it, the SOP is marked as read.',
      education: 'Start the training; it counts as completed once you’ve watched at least half of the video. Training past its deadline can’t be opened.',
      forms: 'Tapping a form opens its file in a new tab, where you can download or print it.',
    },
    sopFilter: {
      label: 'SOP filter',
      all: 'All',
      unread_one: 'Unread ({{count}})',
      unread_other: 'Unread ({{count}})',
      read_one: 'Read ({{count}})',
      read_other: 'Read ({{count}})',
    },
    search: {
      sop: 'Search SOPs...',
      education: 'Search training...',
      forms: 'Search forms...',
      policies: 'Search policies...',
      jobDescriptions: 'Search job descriptions...',
    },
    empty: {
      sop: 'No SOPs are assigned to you.',
      education: 'No training is assigned to you.',
      forms: 'There are no forms for you.',
      policies: 'No policies are assigned to you.',
      jobDescriptions: 'No job descriptions are assigned to you.',
    },
    untitled: {
      policies: 'Untitled policy',
      jobDescriptions: 'Untitled job description',
    },
    noMatch: 'Nothing matches "{{term}}".',
    loadError: 'The documents could not be loaded.',
    kinds: {
      pdf: 'PDF document',
      video: 'Video',
      ppt: 'Presentation',
      other: 'File',
    },
    badges: {
      new: 'New',
      read: 'Read',
      unread: 'Unread',
      completed: 'Completed',
      expired: 'Expired',
    },
    deadline: 'Deadline: {{date}}',
    download: 'Download',
    downloadNamed: 'Download {{name}}',
    version: 'Version {{version}}',
    noFile: 'There is no file for this document.',
    cannotPreview: 'This file type can’t be previewed here; please download it to open it.',
    viewer: {
      title: 'SOP document viewer',
      version: 'Version {{version}}',
      openInTab: 'Open in new tab',
      noVideo: 'Your browser can’t play this video.',
      cannotPreview: 'This file type can’t be previewed here. Download and read it, then confirm.',
      download: 'Download file',
      confirm: 'I have read and understood this SOP',
      lockedHint: 'The checkbox unlocks a few seconds after the document loads (for a video, when it ends). Please read the document to the end.',
      quizHint: 'Confirming opens a short quiz for your department.',
      readHint: 'This SOP is recorded as read. Tap the box again to take the confirmation back.',
    },
    toast: {
      markedRead: 'The SOP was marked as read.',
      markedUnread: 'The SOP was marked as unread.',
      actionError: 'Something went wrong.',
      noFormFile: 'There is no file for this form.',
      trainingDone: 'Training completed!',
    },
  },
  projects: {
    title: 'My Projects',
    description: 'The projects you work on. Tap a project to see your visits in it.',
    hidePassive_one: 'Hide the inactive project',
    hidePassive_other: 'Hide inactive projects ({{count}})',
    showPassive_one: 'Show the inactive project',
    showPassive_other: 'Show inactive projects ({{count}})',
    primer: 'Primary',
    backup: 'Backup',
    blinded: 'Blinded',
    unblinded: 'Unblinded',
    openLabel: 'Open label',
    missingInfo: 'Info missing',
    missingInfoHint: 'On projects marked "Info missing", your type (primary / backup) or blinding hasn’t been entered. Let your project manager know.',
    passive: 'Inactive',
    passiveRole: 'Your role in this project is inactive',
    passiveProject: 'The project is inactive',
    empty: 'You aren’t assigned to any project.',
    emptyHint: 'Projects appear here once you’re assigned.',
    allPassiveHidden: 'All your projects are inactive and hidden.',
  },
  leave: {
    types: {
      yillik: 'Annual Leave',
      mazeret: 'Personal Leave',
      raporlu: 'Sick Leave',
      dogum_oncesi: 'Prenatal Leave',
      babalik: 'Paternity Leave',
      evlilik: 'Marriage Leave',
      olum: 'Bereavement Leave',
      engelli_cocuk: 'Disabled Child Care Leave',
      dogum_sonrasi: 'Postnatal Leave',
      yatakli_tedavi: 'Inpatient Treatment',
      refakat: 'Caregiver Leave',
      sua: 'Radiation Leave',
      saglik_kurulu: 'Medical Board Report',
      ayakta_tedavi: 'Outpatient Treatment',
    },
    status: {
      PENDING: 'Pending',
      APPROVED: 'Approved',
      REJECTED: 'Rejected',
    },
    toast: {
      loadError: 'Your leave requests could not be loaded.',
    },
    form: {
      title: 'Request Leave',
      description: 'Your request goes to your manager for approval; follow it under "My Leave Requests".',
      type: 'Leave type',
      typeHints: {
        yillik: 'Taken from your annual balance',
        mazeret: 'Short personal reasons',
        raporlu: 'With a medical report; you can attach it',
      },
      balance: {
        entitlement: 'Entitlement',
        used: 'Used',
        reserved: 'Awaiting approval',
        remaining: 'Remaining',
      },
      noBalance: 'You have no annual leave left, so you can’t send a new annual leave request.',
      start: 'Start date',
      end: 'End date',
      endHint: 'The last day of leave',
      days: 'Leave days',
      daysHint: 'Counted from the dates: Saturday is half a day; Sundays and public holidays don’t count.',
      travelDays: 'Travel days',
      travelDaysHint: 'If any; half days allowed (0.5)',
      overBalance: 'The days you requested ({{requested}}) are more than your remaining balance ({{remaining}}).',
      reason: 'Reason',
      reasonPlaceholder: 'Explain briefly',
      address: 'Address during leave',
      addressPlaceholder: 'City / address',
      addressHint: 'So you can be reached if needed',
      report: 'Report file',
      reportHint: 'Optional · PDF, JPG, PNG, DOC',
      chooseReport: 'Choose file',
      removeReport: 'Remove file',
      approvalNote: 'Once sent, your request shows as "Pending"; when approved it also appears on your calendar.',
      submit: 'Send Leave Request',
      sending: 'Sending...',
      sent: 'Your leave request was sent. Your manager will review it.',
      errors: {
        dates: 'Please enter the start and end dates.',
        user: 'Your user details were not found.',
        days: 'Choose a valid leave length.',
        balance: 'Not enough annual leave. Remaining: {{remaining}} days, requested: {{requested}} days.',
        send: 'The leave request could not be sent. Please try again.',
      },
    },
    list: {
      title: 'My Leave Requests',
      description: 'The leave you requested and the leave your manager entered for you, with status and the manager’s note.',
      newRequest: 'New Request',
      filterLabel: 'Filter by status',
      all: 'All',
      empty: 'You have no leave requests yet.',
      emptyHint: 'Tap "New Request" to request leave.',
      noMatch: 'No leave requests with this status.',
      sentOn: 'Sent on {{date}}',
      unknownDate: 'Date unknown',
      sourceRequest: 'Leave request',
      sourceAdmin: 'Entered by a manager',
      start: 'Start',
      end: 'End',
      days: 'Length',
      travelDays: 'Travel',
      dayCount_one: '{{count}} day',
      dayCount_other: '{{count}} days',
      reason: 'Reason',
      address: 'Address',
      report: 'Report file',
      adminNote: 'Manager’s note',
      createdByAdmin: 'This leave was entered by your manager.',
      notReviewed: 'Not reviewed yet',
      reviewedBy: 'Reviewed by {{name}}',
    },
  },
  expense: {
    categories: {
      transport: 'Transport',
      food: 'Meals',
      accommodation: 'Accommodation',
      supplies: 'Supplies',
      training: 'Training',
      other: 'Other',
    },
    status: {
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
    },
    form: {
      title: 'Claim an Expense',
      description: 'Send an expense with its receipts; it goes to the finance managers for approval.',
      amount: 'Amount (₺)',
      category: 'Category',
      descriptionLabel: 'Description',
      descriptionPlaceholder: 'What was it for, where and when?',
      documents: 'Documents (PDF)',
      documentsHint: 'Optional · add receipts and invoices as PDF',
      approvalNote: 'Your claim reaches the finance managers as a task and an e-mail. You can edit it under "My Expenses" until it’s approved.',
      submit: 'Send Expense Claim',
      sending: 'Sending...',
      uploading: 'Uploading PDFs...',
      successTitle: 'Your expense claim was sent',
      successText: 'Your claim was created and sent to the finance managers for approval. Follow it under "My Expenses".',
      goTracking: 'Go to My Expenses',
      newRequest: 'New Claim',
      errors: {
        amountRequired: 'The amount is required',
        amountPositive: 'The amount must be greater than 0',
        descriptionRequired: 'A description is required',
        required: 'Please fill in the required fields.',
        user: 'Your user details were not found.',
        upload: 'The PDF could not be uploaded.',
        uploadDetail: 'The PDF could not be uploaded: {{message}}',
        send: 'The expense claim could not be sent: {{message}}',
      },
    },
    pdf: {
      choose: 'Choose PDF files',
      limits: 'You can choose several files · 10 MB each at most',
      selected_one: 'PDF file selected.',
      selected_other: '{{count}} PDF files selected.',
      selectedTitle_one: 'Selected file',
      selectedTitle_other: 'Selected files ({{count}})',
      removeAll: 'Remove all',
      allRemoved: 'All files removed.',
      removed: 'File removed.',
      removeOne: 'Remove {{name}}',
      notPdf: '{{name}}: only PDF files can be uploaded.',
      tooLarge: '{{name}}: the file must be smaller than 10 MB.',
    },
    tracking: {
      title: 'My Expenses',
      description: 'Your expense claims and their status. Pending claims can still be edited.',
      newRequest: 'New Claim',
      total: 'Total',
      all: 'All',
      filterLabel: 'Filter by status',
      empty: 'You have no expense claims yet.',
      emptyHint: 'Create one under "Claim Expense".',
      noMatch: 'No expense claims with this status.',
      edit: 'Edit',
      pendingHint: 'Awaiting approval',
      rejectionReason: 'Reason for rejection',
      processedOn: 'Processed on {{date}}',
      documentN: 'Document {{n}}',
    },
    approvals: {
      help: 'Review pending claims together with their documents; approve them if they’re fine, otherwise reject them with a reason. The reason is shared with the employee.',
      helpOwn: 'The expense claims you sent. You can delete a claim that hasn’t been processed yet.',
      titleAll: 'All Expense Claims',
      titleOwn: 'My Expense Claims',
      count_one: '{{count}} claim',
      count_other: '{{count}} claims',
      refresh: 'Refresh',
      search: 'Search employee, category or description...',
      loading: 'Loading expense claims',
      stats: {
        label: 'Claim summary and quick filter',
        all: 'Total',
        pending: 'Pending',
        approved: 'Approved',
        rejected: 'Rejected',
        pendingAmount: 'Awaiting Approval',
        pendingAmountShort: 'Pending ₺',
      },
      empty: 'No expense claims yet',
      emptyHint: 'Claims appear here once employees create them from "Claim Expense".',
      emptyOwnHint: 'Use the "Claim Expense" tab to create an expense claim.',
      noMatch: 'No matching expense claims',
      noMatchHint: 'Try a different search or filter.',
      clearFilters: 'Clear the filter',
      noDate: 'No date',
      approve: 'Approve',
      reject: 'Reject',
      delete: 'Delete',
      documents: 'Documents ({{count}})',
      documentN: 'Document {{n}}',
      view: 'View {{name}}',
      download: 'Download {{name}}',
      rejectionReason: 'Reason for rejection',
      readReason: 'Read all',
      editReason: 'Edit the reason',
      processedOn: 'Processed on {{date}}',
      reasonDialog: {
        title: 'Reason for Rejection',
        close: 'Close',
      },
      editDialog: {
        title: 'Edit the Reason for Rejection',
        text: 'Update the reason; the employee sees the new one on their claim.',
        label: 'Reason for Rejection',
        placeholder: 'Write the reason for rejection here...',
        cancel: 'Cancel',
        save: 'Save',
      },
      rejectDialog: {
        title: 'Reject the Expense Claim',
        text: 'Please explain why you’re rejecting this claim. The employee will see this.',
        label: 'Reason for Rejection',
        placeholder: 'e.g. Missing receipt, wrong amount, unapproved spending...',
        cancel: 'Cancel',
        confirm: 'Reject',
      },
      deleteDialog: {
        title: 'Delete this expense claim?',
        message: 'Are you sure you want to delete this expense claim?',
        confirm: 'Yes, delete',
        cancel: 'Cancel',
      },
      toast: {
        loadError: 'Something went wrong while loading the expense claims.',
        deleted: 'Expense claim deleted!',
        deleteError: 'Something went wrong while deleting the claim: {{message}}',
        noPermission: 'You don’t have permission for this action.',
        approved: 'Expense claim approved!',
        statusError: 'Something went wrong while updating the status: {{message}}',
        reasonRequired: 'Please enter the reason for rejection.',
        rejected: 'Expense claim rejected!',
        pdfMissing: 'PDF file not found',
        reasonUpdated: 'Reason for rejection updated!',
        updateError: 'Something went wrong while saving: {{message}}',
      },
    },
    edit: {
      title: 'Edit Expense Claim',
      subtitle: 'You can change it until it’s approved',
      existing: 'Attached documents',
      removeExisting: 'Remove documents',
      existingRemoved: 'The attached documents will be removed.',
      view: 'View',
      download: 'Download',
      downloading: 'Downloading PDF...',
      documentN: 'Document {{n}}',
      replace: 'Replace documents',
      replaceHint: 'New PDFs replace the attached documents.',
      add: 'Add a PDF document',
      saving: 'Saving...',
      saved: 'The expense claim was updated.',
      saveError: 'The expense claim could not be updated: {{message}}',
    },
  },
  messages: {
    title: 'Messages',
    description: 'Chat with your colleagues, share files and start calls.',
    unread_one: 'You have {{count}} unread message',
    unread_other: 'You have {{count}} unread messages',
    load: 'Open Messages',
    loading: 'Loading messages...',
  },
  notifications: {
    title: 'Announcements',
    description: 'Company-wide announcements. The newest are at the top.',
    empty: 'No announcements right now.',
    emptyHint: 'New announcements show up here.',
    delete: 'Delete',
    deleteTitle: 'Delete this announcement?',
    deleteMessage: 'This announcement will be removed for everyone. This cannot be undone.',
    admin: {
      pinTitle: 'New announcement',
      pinHint: 'Shows in everyone’s Announcements.',
      pinPlaceholder: 'Announcement text...',
      pinSubmit: 'Publish',
      broadcastTitle: 'Message everyone',
      broadcastHint: 'Arrives as a system message in the inbox of every employee who isn’t an admin.',
      broadcastPlaceholder: 'Message for all employees...',
      broadcastSubmit: 'Send to Everyone',
      broadcastConfirmTitle: 'Send to everyone?',
      broadcastConfirm: 'This message will go to the inbox of every employee except admins.',
    },
    toast: {
      pinned: 'Announcement published.',
      pinError: 'The announcement could not be published.',
      broadcastSent_one: 'The message was sent to 1 person.',
      broadcastSent_other: 'The message was sent to {{count}} people.',
      broadcastError: 'The message could not be sent: {{message}}',
      deleted: 'Announcement deleted.',
      deleteError: 'The announcement could not be deleted.',
    },
  },
  errorReport: {
    title: 'Report a Bug',
    description: 'Tell the software team about a problem you ran into on the portal.',
    tips: 'Write what you were trying to do, on which page, and what happened. A screenshot helps us fix it much faster.',
    summary: 'What went wrong',
    summaryPlaceholder: 'e.g. When I press "Save" while adding a visit, the page stops responding...',
    required: 'Please describe the problem.',
    screenshot: 'Screenshot',
    screenshotHint: 'Optional',
    chooseScreenshot: 'Choose image',
    removeScreenshot: 'Remove image',
    submit: 'Send Report',
    sending: 'Sending...',
    sent: 'Your report was sent. Thank you!',
    sendError: 'The report could not be sent.',
  },
  complaints: {
    types: {
      sikayet: 'Complaint',
      talep: 'Request',
      itiraz: 'Objection',
      mobbing: 'Mobbing Report',
      cinsel_taciz: 'Sexual Harassment Report',
      sozlu_taciz: 'Verbal Harassment Report',
      fiziksel_taciz: 'Physical Harassment Report',
      psikolojik_siddet: 'Psychological Abuse Report',
      ayrimcilik: 'Discrimination Report',
      is_guvenligi: 'Workplace Safety Report',
      etik_ihlali: 'Ethics Violation Report',
      oneri: 'Suggestion',
      diger: 'Other',
    },
    status: {
      PENDING: 'Pending',
      INCELENIYOR: 'Under review',
      RESOLVED: 'Resolved',
      CLOSED: 'Closed',
    },
    to: 'To: {{names}}',
    replies_one: 'Reply (1)',
    replies_other: 'Replies ({{count}})',
    downloadDocx: 'Download petition (DOCX)',
    openDetail: 'Open petition: {{subject}}',
    toast: {
      historyError: 'Your petitions could not be loaded.',
      incomingError: 'Incoming petitions could not be loaded.',
    },
    form: {
      title: 'Write a Petition',
      description: 'Create a formal petition; it goes to the people you choose and a copy is kept as a document (DOCX).',
      heading: 'PETITION',
      subheading: 'Formal application form',
      targets: 'Send to',
      authority: 'In charge',
      testMode: 'Test mode',
      sender: 'Applicant',
      name: 'Name',
      email: 'E-mail',
      title2: 'Title',
      department: 'Department',
      loading: 'Loading...',
      type: 'Petition type',
      typePlaceholder: 'Choose the petition type',
      subject: 'Subject',
      subjectPlaceholder: 'The subject of your petition',
      details: 'Petition text',
      greeting: 'Dear Sir or Madam,',
      detailsPlaceholder: 'Write the details of your application clearly...',
      closing1: 'I kindly ask for the necessary action.',
      closing2: 'Sincerely,',
      privacy: 'Your petition is handled confidentially and only reviewed by the people in charge.',
      submit: 'Send Petition',
      sending: 'Sending...',
      sent: 'Your petition was sent.',
      errors: {
        session: 'Session error. Please refresh the page.',
        type: 'Please choose a petition type.',
        content: 'Please fill in the subject and the text.',
        target: 'Please choose at least one recipient.',
        send: 'The petition could not be sent: {{message}}',
      },
    },
    history: {
      title: 'My earlier petitions',
      description: 'The status of the petitions you sent and the replies from the people in charge.',
      empty: 'You have no petitions yet.',
    },
    incoming: {
      title: 'Incoming Petitions',
      descriptionAdmin: 'Every petition sent through the portal. Tap one to change its status and reply.',
      descriptionTarget: 'Petitions sent to you. Tap one to change its status and reply.',
      filterLabel: 'Filter by status',
      all: 'All',
      search: 'Search subject, person or number...',
      empty: 'No incoming petitions yet.',
      noMatch: 'No petitions match this filter.',
      detailTitle: 'Petition details',
      sender: 'From',
      date: 'Date',
      type: 'Type',
      currentStatus: 'Current status',
      targets: 'To',
      details: 'Petition text',
      changeStatus: 'Change status',
      current: 'Current',
      statusUpdated: 'The petition status was updated.',
      statusError: 'The petition status could not be updated.',
      writeReply: 'Write a reply',
      replyPlaceholder: 'Your reply to the applicant...',
      replyRequired: 'Please write a reply.',
      sendReply: 'Send Reply',
      sending: 'Sending...',
      replySent: 'Your reply was sent.',
      replyError: 'The reply could not be sent.',
    },
  },
};

export default profilePage;
